import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LibraryMusicOutlined from "@mui/icons-material/LibraryMusicOutlined";
import StopCircleOutlined from "@mui/icons-material/StopCircleOutlined";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";
import ReactSelect from "react-select";
import MusicLibraryPanel from "./MusicLibraryPanel";

const MODEL_OPTIONS = [
  { value: "V5_5", label: "V5_5 — Unleash your voice" },
  { value: "V5", label: "V5" },
  { value: "V4_5ALL", label: "V4_5ALL" },
  { value: "V4_5PLUS", label: "V4_5PLUS" },
  { value: "V4_5", label: "V4_5" },
  { value: "V4", label: "V4" },
  { value: "V3_5", label: "V3_5" },
];

const DEFAULT_LYRICS = `**Verse 1**
This Book of the Law shall not depart from my mouth,
I will speak Your Word in every season.
I will ponder on it day and night,
Finding wisdom, truth, and direction.

**Pre-Chorus**
Your promises are my guide,
Your commandments are my light.
When I walk according to Your ways,
You will lead me to victory.

**Chorus**
Meditate day and night,
Keep Your Word before my eyes.
Then my way will prosper,
And I will have good success.
Lord, Your truth will be my song,
In Your ways I will be strong.
Meditate day and night,
And You will bless my path.

**Verse 2**
When the road ahead seems long,
Your Word gives courage to my soul.
Every promise You have spoken
Will sustain and make me whole.

**Pre-Chorus**
Your promises are my guide,
Your commandments are my light.
When I walk according to Your ways,
You will lead me to victory.

**Chorus**
Meditate day and night,
Keep Your Word before my eyes.
Then my way will prosper,
And I will have good success.
Lord, Your truth will be my song,
In Your ways I will be strong.
Meditate day and night,
And You will bless my path.

**Bridge**
I will not turn to the left or right,
Your Word will be my delight.
With faith and obedience,
I will walk in Your promises.

**Final Chorus**
Meditate day and night,
Keep Your Word before my eyes.
Then my way will prosper,
And I will have good success.
Lord, Your truth will be my song,
In Your ways I will be strong.
Meditate day and night,
Forever in Your Word.
`;

const FAIL_STATUSES = new Set([
  "CREATE_TASK_FAILED",
  "GENERATE_AUDIO_FAILED",
  "CALLBACK_EXCEPTION",
  "SENSITIVE_WORD_ERROR",
]);

const LYRICS_FAIL_STATUSES = new Set([
  "CREATE_TASK_FAILED",
  "GENERATE_LYRICS_FAILED",
  "CALLBACK_EXCEPTION",
  "SENSITIVE_WORD_ERROR",
]);

function pickAudioUrl(track) {
  if (!track || typeof track !== "object") return null;
  return (
    (typeof track.audioUrl === "string" && track.audioUrl) ||
    (typeof track.streamAudioUrl === "string" && track.streamAudioUrl) ||
    null
  );
}

function MusicStudio({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [model, setModel] = React.useState("V5_5");
  const [customMode, setCustomMode] = React.useState(true);
  const [instrumental, setInstrumental] = React.useState(false);
  const [title, setTitle] = React.useState("Meditate Day and Night");
  const [style, setStyle] = React.useState("Gospel");
  const [prompt, setPrompt] = React.useState(DEFAULT_LYRICS);
  const [vocalGender, setVocalGender] = React.useState("");
  const [audioWeight, setAudioWeight] = React.useState(0.65);
  const [personaModel, setPersonaModel] = React.useState("style_persona");
  const [personaId, setPersonaId] = React.useState("");
  const [negativeTags, setNegativeTags] = React.useState("");
  const [advancedOpen, setAdvancedOpen] = React.useState(true);

  const [genreOptions, setGenreOptions] = React.useState([]);
  const [genreSelect, setGenreSelect] = React.useState(null);
  const [lyricsSource, setLyricsSource] = React.useState("own");
  const [lyricsTaskId, setLyricsTaskId] = React.useState("");
  const [lyricJobId, setLyricJobId] = React.useState(null);
  const [lyricsPolling, setLyricsPolling] = React.useState(false);
  const [lyricsStatus, setLyricsStatus] = React.useState("");
  const [lyricsPollError, setLyricsPollError] = React.useState("");
  const [lyricsSubmitting, setLyricsSubmitting] = React.useState(false);
  const lyricsPollRef = React.useRef(null);
  const lyricsPollTries = React.useRef(0);
  const lyricsPromptRef = React.useRef(null);

  const [submitting, setSubmitting] = React.useState(false);
  const [songJobId, setSongJobId] = React.useState(null);
  const [linkedLyricJobId, setLinkedLyricJobId] = React.useState(null);
  const [studioTab, setStudioTab] = React.useState("studio");
  const [taskId, setTaskId] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [pollError, setPollError] = React.useState("");
  const [sunoData, setSunoData] = React.useState([]);
  const [polling, setPolling] = React.useState(false);
  const pollRef = React.useRef(null);
  const pollTries = React.useRef(0);

  React.useEffect(() => {
    const onPrepare = (e) => {
      if (e.detail?.tourId === "music") setStudioTab("studio");
    };
    window.addEventListener("beyundai:prepare-tour", onPrepare);
    return () => window.removeEventListener("beyundai:prepare-tour", onPrepare);
  }, []);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  const stopLyricsPolling = React.useCallback(() => {
    if (lyricsPollRef.current) {
      clearInterval(lyricsPollRef.current);
      lyricsPollRef.current = null;
    }
    setLyricsPolling(false);
  }, []);

  React.useEffect(() => {
    return () => {
      stopPolling();
      stopLyricsPolling();
    };
  }, [stopPolling, stopLyricsPolling]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/music/genres",
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (cancelled || !res?.data?.success || !Array.isArray(res.data.data)) return;
      setGenreOptions(
        res.data.data.map((g) => ({
          value: g.id,
          label: g.name,
          slug: g.slug,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  const fetchStatus = React.useCallback(async () => {
    if (songJobId != null) {
      const res = await hitAxios({
        path: `/api/music/songs/jobs/${songJobId}/poll`,
        post: true,
        obj: {},
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        setPollError(res?.data?.msg || "Status request failed");
        return;
      }
      const d = res.data.data || {};
      const job = d.job || {};
      const finalized = Boolean(d.finalized);
      const st = job.status === "completed" ? "SUCCESS" : String(job.status || "");
      setStatus(st);
      if (job.kie_task_id) setTaskId(String(job.kie_task_id));
      const errMsg = typeof job.error_message === "string" ? job.error_message : "";
      if (FAIL_STATUSES.has(st) || job.status === "failed") {
        setPollError(errMsg || st || "failed");
        stopPolling();
        return;
      }
      setPollError("");
      const tracks = Array.isArray(job.tracks) ? job.tracks : [];
      if (tracks.length) {
        setSunoData(
          tracks.map((t, i) => ({
            id: t.stored || i,
            title: t.title,
            audioUrl: t.stored,
            streamAudioUrl: t.stored,
          })),
        );
      }
      if (finalized) stopPolling();
      return;
    }

    if (!taskId.trim()) return;
    const res = await hitAxios({
      path: `/api/music/status?taskId=${encodeURIComponent(taskId.trim())}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      setPollError(res?.data?.msg || "Status request failed");
      return;
    }
    const d = res.data.data;
    const st = typeof d?.status === "string" ? d.status : "";
    setStatus(st);
    const errMsg = typeof d?.errorMessage === "string" ? d.errorMessage : "";
    if (FAIL_STATUSES.has(st)) {
      setPollError(errMsg || st);
      stopPolling();
      return;
    }
    setPollError("");
    const tracks = d?.response?.sunoData;
    if (Array.isArray(tracks)) setSunoData(tracks);

    if (st === "SUCCESS") {
      stopPolling();
    }
  }, [hitAxios, taskId, songJobId, stopPolling]);

  React.useEffect(() => {
    if (!polling || (songJobId == null && !taskId.trim())) return undefined;
    pollTries.current = 0;
    fetchStatus();
    pollRef.current = setInterval(() => {
      pollTries.current += 1;
      if (pollTries.current > 90) {
        stopPolling();
        setPollError(lang?.musicPollTimeout || "Stopped polling after several minutes.");
        return;
      }
      fetchStatus();
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [polling, taskId, songJobId, fetchStatus, stopPolling, lang?.musicPollTimeout]);

  const fetchLyricsStatus = React.useCallback(async () => {
    if (lyricJobId != null) {
      const res = await hitAxios({
        path: `/api/music/lyrics/jobs/${lyricJobId}/poll`,
        post: true,
        obj: {},
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        setLyricsPollError(res?.data?.msg || "Status request failed");
        return;
      }
      const d = res.data.data || {};
      const job = d.job || {};
      const finalized = Boolean(d.finalized);
      const mapSt =
        job.status === "ready"
          ? "SUCCESS"
          : job.status === "failed"
            ? "GENERATE_LYRICS_FAILED"
            : "PENDING";
      setLyricsStatus(mapSt);
      if (job.kie_task_id) setLyricsTaskId(String(job.kie_task_id));
      const errMsg = typeof job.error_message === "string" ? job.error_message : "";
      if (job.status === "failed") {
        setLyricsPollError(errMsg || "Lyrics generation failed.");
        stopLyricsPolling();
        return;
      }
      setLyricsPollError("");
      if (finalized && job.lyrics_text) {
        setPrompt(String(job.lyrics_text));
        setLyricsSource("own");
        stopLyricsPolling();
        showSnack?.(lang?.musicLyricsReady || "Lyrics ready — review then generate music.", true);
        requestAnimationFrame(() => {
          const el = lyricsPromptRef.current;
          if (!el) return;
          el.focus({ preventScroll: true });
          el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
      } else if (finalized && job.status === "ready" && !job.lyrics_text) {
        stopLyricsPolling();
        setLyricsPollError(
          lang?.musicLyricsParseFail || "Lyrics finished but the app could not read the text. Try again.",
        );
      }
      return;
    }

    if (!lyricsTaskId.trim()) return;
    const res = await hitAxios({
      path: `/api/music/lyrics/status?taskId=${encodeURIComponent(lyricsTaskId.trim())}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      setLyricsPollError(res?.data?.msg || "Status request failed");
      return;
    }
    const d = res.data.data;
    const st = typeof d?.status === "string" ? d.status : "";
    setLyricsStatus(st);
    const errMsg = typeof d?.errorMessage === "string" ? d.errorMessage : "";
    if (LYRICS_FAIL_STATUSES.has(st)) {
      setLyricsPollError(errMsg || st);
      stopLyricsPolling();
      return;
    }
    setLyricsPollError("");
    const done = st === "SUCCESS" || st === "TEXT_SUCCESS";

    let lyricsText = typeof d?.normalizedLyrics === "string" ? d.normalizedLyrics.trim() : "";
    if (!lyricsText && d?.response && typeof d.response === "object") {
      const r = d.response;
      if (Array.isArray(r.data)) {
        const texts = r.data
          .filter((x) => {
            if (!x || typeof x !== "object") return false;
            const rowSt = typeof x.status === "string" ? x.status.toLowerCase() : "";
            if (rowSt === "failed") return false;
            if (typeof x.text !== "string" || !String(x.text).trim()) return false;
            if (done) return true;
            return rowSt === "complete" || rowSt === "completed" || rowSt === "success";
          })
          .map((x) => {
            const tx = String(x.text).trim();
            const ti = typeof x.title === "string" ? x.title.trim() : "";
            return ti ? `**${ti}**\n${tx}` : tx;
          });
        if (texts.length) lyricsText = texts.join("\n\n---\n\n");
      }
      if (!lyricsText && typeof r.text === "string") lyricsText = r.text.trim();
      else if (!lyricsText && typeof r.lyrics === "string") lyricsText = r.lyrics.trim();
    }
    if (done) {
      if (lyricsText) {
        setPrompt(lyricsText);
        setLyricsSource("own");
        stopLyricsPolling();
        showSnack?.(lang?.musicLyricsReady || "Lyrics ready — review then generate music.", true);
        requestAnimationFrame(() => {
          const el = lyricsPromptRef.current;
          if (!el) return;
          el.focus({ preventScroll: true });
          el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
      } else {
        stopLyricsPolling();
        setLyricsPollError(
          lang?.musicLyricsParseFail || "Lyrics finished but the app could not read the text. Try again.",
        );
      }
    }
  }, [
    hitAxios,
    lyricsTaskId,
    lyricJobId,
    stopLyricsPolling,
    showSnack,
    lang?.musicLyricsReady,
    lang?.musicLyricsParseFail,
  ]);

  React.useEffect(() => {
    if (!lyricsPolling || (lyricJobId == null && !lyricsTaskId.trim())) return undefined;
    lyricsPollTries.current = 0;
    fetchLyricsStatus();
    lyricsPollRef.current = setInterval(() => {
      lyricsPollTries.current += 1;
      if (lyricsPollTries.current > 150) {
        stopLyricsPolling();
        setLyricsPollError(lang?.musicLyricsPollTimeout || lang?.musicPollTimeout || "Stopped polling after several minutes.");
        return;
      }
      fetchLyricsStatus();
    }, 4000);
    return () => {
      if (lyricsPollRef.current) clearInterval(lyricsPollRef.current);
    };
  }, [
    lyricsPolling,
    lyricsTaskId,
    lyricJobId,
    fetchLyricsStatus,
    stopLyricsPolling,
    lang?.musicPollTimeout,
    lang?.musicLyricsPollTimeout,
  ]);

  const selectStyles = React.useMemo(() => {
    const paper = theme.palette.background.paper;
    const border = theme.palette.divider;
    const text = theme.palette.text.primary;
    const sub = theme.palette.text.secondary;
    const primary = theme.palette.primary.main;
    const br =
      typeof theme.shape.borderRadius === "number" && !Number.isNaN(theme.shape.borderRadius)
        ? theme.shape.borderRadius
        : 8;
    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : paper,
        borderColor: state.isFocused ? primary : border,
        borderRadius: br,
        boxShadow: state.isFocused ? `0 0 0 1px ${alpha(primary, 0.35)}` : "none",
        minHeight: 40,
        cursor: "pointer",
        "&:hover": { borderColor: alpha(primary, 0.5) },
      }),
      menuPortal: (base) => ({ ...base, zIndex: theme.zIndex.modal + 1 }),
      menu: (base) => ({
        ...base,
        backgroundColor: paper,
        border: `1px solid ${border}`,
        borderRadius: br,
        boxShadow: theme.shadows[12],
        overflow: "hidden",
      }),
      menuList: (base) => ({ ...base, padding: theme.spacing(0.5) }),
      option: (base, state) => ({
        ...base,
        borderRadius: 1,
        backgroundColor: state.isSelected
          ? alpha(primary, theme.palette.mode === "dark" ? 0.28 : 0.18)
          : state.isFocused
            ? alpha(primary, 0.12)
            : "transparent",
        color: text,
        cursor: "pointer",
      }),
      singleValue: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: sub }),
      input: (base) => ({ ...base, color: text }),
      indicatorsContainer: (base) => ({ ...base, color: sub }),
      dropdownIndicator: (base) => ({
        ...base,
        color: sub,
        "&:hover": { color: text },
      }),
      clearIndicator: (base) => ({
        ...base,
        color: sub,
        "&:hover": { color: text },
      }),
    };
  }, [theme]);

  async function handleGenerate() {
    if (!instrumental && customMode && lyricsSource === "kie" && !prompt.trim()) {
      showSnack?.(
        lang?.musicNeedLyrics ||
          "Generate lyrics first, or switch to “I have my lyrics” and paste your text.",
        false,
      );
      return;
    }

    setSubmitting(true);
    setPollError("");
    setStatus("");
    setSunoData([]);
    stopPolling();
    setTaskId("");
    setSongJobId(null);

    const payload = {
      model,
      customMode,
      instrumental,
      prompt: prompt.trim(),
      title: title.trim(),
      style: style.trim(),
      audioWeight,
    };
    if (vocalGender === "m" || vocalGender === "f") payload.vocalGender = vocalGender;
    if (personaModel.trim()) payload.personaModel = personaModel.trim();
    if (personaId.trim()) payload.personaId = personaId.trim();
    if (negativeTags.trim()) payload.negativeTags = negativeTags.trim();
    if (linkedLyricJobId != null) payload.lyric_job_id = linkedLyricJobId;

    const res = await hitAxios({
      path: "/api/music/songs/jobs",
      post: true,
      obj: payload,
      showLoading: false,
    });
    setSubmitting(false);
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Generate failed", false);
      return;
    }
    const job = res.data.data?.job;
    if (job?.id) {
      setSongJobId(job.id);
      if (job.kie_task_id) setTaskId(String(job.kie_task_id));
      setPolling(true);
    }
  }

  async function handleStartLyrics() {
    if (!title.trim()) {
      showSnack?.(lang?.musicLyricsNeedTitle || "Enter a song title first.", false);
      return;
    }
    if (!genreSelect?.value) {
      showSnack?.(lang?.musicLyricsNeedGenre || "Choose a genre.", false);
      return;
    }
    setLyricsSubmitting(true);
    setLyricsPollError("");
    setLyricsStatus("");
    stopLyricsPolling();
    setLyricsTaskId("");
    setLyricJobId(null);
    const obj = {
      title: title.trim(),
      genre_id: genreSelect.value,
    };
    const res = await hitAxios({
      path: "/api/music/lyrics/jobs",
      post: true,
      obj,
      showLoading: false,
    });
    setLyricsSubmitting(false);
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Lyrics request failed", false);
      return;
    }
    const job = res.data.data?.job;
    if (job?.id) {
      setLyricJobId(job.id);
      if (job.kie_task_id) setLyricsTaskId(String(job.kie_task_id));
      setLyricsPolling(true);
    }
  }

  function copyTask() {
    if (!taskId) return;
    navigator.clipboard?.writeText(taskId).then(
      () => showSnack?.("Copied", true),
      () => showSnack?.("Copy failed", false),
    );
  }

  function copyLyricsTask() {
    if (!lyricsTaskId) return;
    navigator.clipboard?.writeText(lyricsTaskId).then(
      () => showSnack?.("Copied", true),
      () => showSnack?.("Copy failed", false),
    );
  }

  const statusColor =
    status === "SUCCESS"
      ? "success"
      : FAIL_STATUSES.has(status)
        ? "error"
        : status === "FIRST_SUCCESS" || status === "TEXT_SUCCESS"
          ? "info"
          : "default";

  const lyricsStatusColor =
    lyricsStatus === "SUCCESS" || lyricsStatus === "TEXT_SUCCESS"
      ? "success"
      : LYRICS_FAIL_STATUSES.has(lyricsStatus)
        ? "error"
        : lyricsStatus === "PENDING"
          ? "info"
          : "default";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <PageHeader
        title={lang?.musicTitle || "Music Studio"}
        subtitle={
          lang?.musicSubtitle ||
          "Searchable genres..."
        }
        icon={LibraryMusicOutlined}
        gradientIcon
      />


      <Tabs
        value={studioTab}
        onChange={(_, v) => setStudioTab(v)}
        data-tour="music-tabs"
        sx={{ mb: 2 }}
      >
        <Tab
          value="studio"
          data-tour="music-tab-studio"
          label={lang?.musicTabStudio || "Studio"}
        />
        <Tab
          value="library"
          data-tour="music-tab-library"
          label={lang?.musicTabLibrary || "Library"}
        />
      </Tabs>

      {studioTab === "library" && (
        <MusicLibraryPanel
          lang={lang}
          linkedLyricJobId={linkedLyricJobId}
          onClearLinkedLyric={() => setLinkedLyricJobId(null)}
          onUseLyricInStudio={(row) => {
            if (!row?.lyrics_text) return;
            setTitle(String(row.title || "").trim() || title);
            setPrompt(String(row.lyrics_text));
            setLinkedLyricJobId(row.id);
            if (row.genre_id != null && Array.isArray(genreOptions)) {
              const g = genreOptions.find((x) => x.value === row.genre_id);
              if (g) setGenreSelect(g);
            }
            setStudioTab("studio");
            showSnack?.(lang?.musicLoadedLyric || "Lyrics loaded in the studio.", true);
          }}
        />
      )}

      {studioTab === "studio" && (
      <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            data-tour="music-compose"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: "100%",
            }}
          >
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Composition
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{lang?.musicModel || "Model"}</InputLabel>
                  <Select
                    label={lang?.musicModel || "Model"}
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  >
                    {MODEL_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={customMode}
                      onChange={(e) => setCustomMode(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={lang?.musicCustomMode || "Custom mode"}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={instrumental}
                      onChange={(e) => setInstrumental(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={lang?.musicInstrumental || "Instrumental"}
                />

                {customMode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {lang?.musicGenreSelectLabel || "Genre"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {lang?.musicGenreSelectHint ||
                        "Matches your theme. With vocals on, your pick fills the Style field."}
                    </Typography>
                    <ReactSelect
                      options={genreOptions}
                      value={genreSelect}
                      onChange={(opt) => {
                        setGenreSelect(opt);
                        if (opt?.label && !instrumental) setStyle(String(opt.label));
                      }}
                      placeholder={lang?.musicGenrePlaceholder || "Type to search…"}
                      isClearable
                      isSearchable
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </Box>
                )}

                {customMode && !instrumental && (
                  <>
                    <TextField
                      label={lang?.musicTitleField || "Title"}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 80 }}
                      helperText={`${title.length}/80`}
                    />
                    <TextField
                      label={lang?.musicStyleField || "Style"}
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setAdvancedOpen((o) => !o)}
                  sx={{ cursor: "pointer", userSelect: "none" }}
                >
                  <Typography variant="subtitle2">{lang?.musicAdvanced || "Advanced"}</Typography>
                  <IconButton size="small" aria-label="toggle advanced">
                    {advancedOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Stack>
                <Collapse in={advancedOpen}>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{lang?.musicVocalGender || "Vocal gender"}</InputLabel>
                      <Select
                        label={lang?.musicVocalGender || "Vocal gender"}
                        value={vocalGender}
                        onChange={(e) => setVocalGender(e.target.value)}
                      >
                        <MenuItem value="">{lang?.musicVocalAny || "Default"}</MenuItem>
                        <MenuItem value="m">Male</MenuItem>
                        <MenuItem value="f">Female</MenuItem>
                      </Select>
                    </FormControl>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {lang?.musicAudioWeight || "Audio weight"}
                      </Typography>
                      <Slider
                        value={audioWeight}
                        onChange={(_, v) => setAudioWeight(v)}
                        min={0}
                        max={1}
                        step={0.01}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <TextField
                      label={lang?.musicPersonaModel || "Persona model"}
                      value={personaModel}
                      onChange={(e) => setPersonaModel(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label={lang?.musicPersonaId || "Persona id"}
                      value={personaId}
                      onChange={(e) => setPersonaId(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label={lang?.musicNegativeTags || "Negative tags"}
                      value={negativeTags}
                      onChange={(e) => setNegativeTags(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                </Collapse>

                {linkedLyricJobId != null && (
                  <Chip
                    size="small"
                    sx={{ mt: 1 }}
                    color="secondary"
                    variant="outlined"
                    label={(lang?.musicLinkedLyricShort || "Linked lyric") + ` #${linkedLyricJobId}`}
                    onDelete={() => setLinkedLyricJobId(null)}
                  />
                )}

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                  onClick={handleGenerate}
                  data-tour="music-generate"
                  sx={{
                    mt: 2,
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                >
                  {lang?.musicGenerate || "Generate"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card
            elevation={0}
            data-tour="music-lyrics"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              minHeight: 420,
            }}
          >
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, display: "block", mb: 1 }}>
                {lang?.musicLyricsColumnTitle || "Lyrics"}
              </Typography>
              <Box
                sx={{
                  p: 1,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`,
                }}
              >
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={lyricsSource}
                  onChange={(_, v) => {
                    if (!v) return;
                    if (v === "kie") setPrompt("");
                    if (v === "own") setPrompt((p) => (p.trim() ? p : DEFAULT_LYRICS));
                    setLyricsSource(v);
                  }}
                  sx={{
                    gap: 0.75,
                    bgcolor: alpha(theme.palette.background.paper, 0.65),
                    borderRadius: 1.5,
                    p: 0.5,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "8px !important",
                      mx: 0,
                    },
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      py: 1.25,
                      px: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      border: "none",
                      "&.Mui-disabled": { opacity: 0.45 },
                      "&.Mui-selected": {
                        color: theme.palette.primary.contrastText,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: theme.shadows[2],
                        "&:hover": {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="own" disableRipple>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ width: "100%" }}>
                      <EditNoteOutlined fontSize="small" />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                          {lang?.musicLyricsOwnShort || "I have lyrics"}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: "block" }}>
                          {lang?.musicLyricsOwnSub || "Paste or edit below"}
                        </Typography>
                      </Box>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="kie" disabled={instrumental || !customMode} disableRipple>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ width: "100%" }}>
                      <AutoAwesomeOutlined fontSize="small" />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                          {lang?.musicLyricsKieShort || "Make lyrics"}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: "block" }}>
                          {lang?.musicLyricsKieSub || "Uses title + genre"}
                        </Typography>
                      </Box>
                    </Stack>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {instrumental && lyricsSource === "kie" && (
                <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
                  {lang?.musicLyricsInstrumentalHint ||
                    "Switch off instrumental and turn on custom mode to generate lyrics."}
                </Alert>
              )}
              {lyricsSource === "kie" && customMode && !instrumental && (
                <Card
                  variant="outlined"
                  sx={{ mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}
                >
                  <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {lang?.musicLyricsStepTitle || "Step 1 — lyrics"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      {lang?.musicLyricsStepBlurb ||
                        "We send your song title and chosen genre. When lyrics are ready, they appear in the editor — then use Generate (left) for the track."}
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={lyricsSubmitting || lyricsPolling}
                      onClick={handleStartLyrics}
                    >
                      {lang?.musicLyricsGenerateBtn || "Generate lyrics"}
                    </Button>

                    {(lyricsTaskId || lyricJobId != null || lyricsPolling || lyricsStatus || lyricsPollError) && (
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.background.default, 0.35),
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {lang?.musicJobLyrics || "Lyrics job"}
                          </Typography>
                          {lyricsPolling && (
                            <Button
                              size="small"
                              startIcon={<StopCircleOutlined />}
                              onClick={() => stopLyricsPolling()}
                            >
                              {lang?.musicStopPoll || "Stop polling"}
                            </Button>
                          )}
                        </Stack>
                        {lyricsPolling && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
                        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                          {lyricJobId != null && (
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" color="text.secondary">
                                {lang?.musicSavedLyricJob || "Saved lyric job"}
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                #{lyricJobId}
                              </Typography>
                            </Stack>
                          )}
                          {lyricsTaskId && (
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" color="text.secondary">
                                {lang?.musicTaskId || "Task id"}
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                                {lyricsTaskId}
                              </Typography>
                              <Tooltip title="Copy">
                                <IconButton size="small" onClick={copyLyricsTask}>
                                  <ContentCopyOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                          {lyricsStatus && (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Typography variant="body2" color="text.secondary">
                                {lang?.musicStatus || "Status"}
                              </Typography>
                              <Chip size="small" label={lyricsStatus} color={lyricsStatusColor} variant="outlined" />
                            </Stack>
                          )}
                          {lyricsPollError && (
                            <Alert severity="warning" variant="outlined">
                              {lyricsPollError}
                            </Alert>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {lang?.musicLyricsPollHint ||
                              "Polls lyrics until status is SUCCESS."}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {lang?.musicLyricsKieTaskNote || ""}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                {lang?.musicLyricsOrPrompt || "Lyrics or prompt"}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                {lyricsSource === "kie" && lyricsPolling
                  ? lang?.musicLyricsWaitingBoxHint ||
                    "When the task finishes, lyrics appear in this box. Edit them, then use Generate music on the left."
                  : lang?.musicLyricsHint || ""}
              </Typography>
              <TextField
                inputRef={lyricsPromptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                fullWidth
                multiline
                minRows={16}
                maxRows={28}
                placeholder={lang?.musicLyricsOrPrompt || "Prompt"}
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {(songJobId != null || taskId || polling || status) && (
        <Card
          elevation={0}
          sx={{
            mt: 3,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {lang?.musicJobMusic || "Music generation"}
              </Typography>
              {polling && (
                <Button
                  size="small"
                  startIcon={<StopCircleOutlined />}
                  onClick={() => {
                    stopPolling();
                  }}
                >
                  {lang?.musicStopPoll || "Stop polling"}
                </Button>
              )}
            </Stack>
            {polling && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {songJobId != null && (
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {lang?.musicSavedJobId || "Saved job"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    #{songJobId}
                  </Typography>
                </Stack>
              )}
              {taskId && (
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {lang?.musicTaskId || "Task id"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                    {taskId}
                  </Typography>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={copyTask}>
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              {status && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    {lang?.musicStatus || "Status"}
                  </Typography>
                  <Chip size="small" label={status} color={statusColor} variant="outlined" />
                </Stack>
              )}
              {pollError && (
                <Alert severity="warning" variant="outlined">
                  {pollError}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                {lang?.musicPollNote || ""}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {sunoData.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {lang?.musicNoTracksYet || "Waiting for audio…"}
              </Typography>
            )}
            <Stack spacing={2}>
              {sunoData.map((tr, i) => {
                const url = pickAudioUrl(tr);
                const t = typeof tr?.title === "string" ? tr.title : `Track ${i + 1}`;
                return (
                  <Box
                    key={tr?.id || i}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      {t}
                    </Typography>
                    {url ? (
                      <Box component="audio" controls src={url} sx={{ width: "100%", mt: 1 }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {lang?.musicNoTracksYet}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </Box>
  );
}

export default MusicStudio;
